import React, { useCallback, useEffect, useRef, useState } from 'react';

// Material UI
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Link from '@material-ui/core/Link';
import Check  from '@material-ui/icons/Check';
import WarningIcon from '@material-ui/icons/Warning';

// Data
import { IColumnMap, IImportMatcher } from './import-matcher.interface';
import { importerMessages as messages } from '../importer.messages';
import { ImportColumnMatcher } from './import-column-matcher.component';
import { Notice } from '../../_ui/icons.component';
import { successColor, warnTextColor } from '../../../../lib/theme';
import { numToAlpha } from '../../../../lib/helpers/conversion.helpers';
import { findNextUnconfirmedColumnIndex } from '../importer.helpers';
import { pluralize, summarizeList } from '../../../../lib/helpers/text.helpers';

// Components
import { IntlMsg } from '../../IntlMsg/intl-msg.component';
import { PrimaryButton } from '../../_ui/buttons.component';
import { Spacer } from '../../_ui/structure.components';
import { Bold } from '../../_ui/typography.component';
import { UnconfirmedMappingsDialog } from './unconfirmed-mappings-dialog.component';

// Row numbers listed in full before the overflow warning collapses the tail to "and N more".
const OVERFLOW_ROWS_LISTED = 4;

export const ImportMatcher: React.FC<IImportMatcher> = ({
  unmetRequirements,
  unmetFields,
  headerRowIndex,
  headerRow,
  columnMap,
  columnOrder,
  overflowColumns,
  overflowRows,
  expectedColumns,
  sample,
  setColumnMapping,
  confirmAllMappings,
  discardUnconfirmedMappings,
  rows,
  completeMatching,
  columnSelectionOptions
}) => {
  const eleMappings = useRef<HTMLHeadingElement>(null);
  const eleActions = useRef<HTMLDivElement>(null);
  const eleColumns = useRef<{ [index: number]: HTMLDivElement | null }>({});
  const [showUnconfirmedDialog, setShowUnconfirmedDialog] = useState(false);
  const [scrollTarget, setScrollTarget] = useState<number | 'actions' | null>(null);

  const unconfirmedColumns: JSX.Element[] = [];
  const confirmedColumns: JSX.Element[] = [];
  // Named, not counted: the dialog tells the user exactly which matches it is about to confirm.
  const unconfirmedMappings: string[] = [];

  // Only a lock change relocates a card; a mapping change in the Select leaves it in place.
  const handleSetColumnMapping = useCallback((columnIndex: number, mapping: IColumnMap) => {
    const wasLocked = !!columnMap[columnIndex]?.isLocked;
    const willLock = !!mapping.isLocked;

    if (willLock && !wasLocked) {
      // The card just left the unconfirmed list; move on to the next decision rather than follow it.
      const nextIndex = findNextUnconfirmedColumnIndex(columnOrder, columnMap, columnIndex);

      setScrollTarget(nextIndex === null ? 'actions' : nextIndex);
    } else if (!willLock && wasLocked) {
      // Editing pulls the card back up out of the confirmed list, so follow it to where it landed.
      setScrollTarget(columnIndex);
    }

    setColumnMapping(columnIndex, mapping);
  }, [columnMap, columnOrder, setColumnMapping]);

  columnOrder.forEach(i => {
    // An overflow column has no header to map against, so it gets no card — the banner reports it.
    if (!columnMap[i]) return;

    const columnSample = sample.map(item => ({
      rowNum: item.rowNum,
      cellData: item.rowData[i]
    }));

    const eleColumn =
      <div key={i} ref={node => { eleColumns.current[i] = node; }}>
        <ImportColumnMatcher
          index={i}
          headerRowIndex={headerRowIndex}
          header={headerRow && headerRow[i]}
          sample={columnSample}
          expectedColumns={expectedColumns}
          currentMapping={columnMap[i]}
          setColumnMapping={handleSetColumnMapping}
          rows={rows}
        />
      </div>;

    if (columnMap[i]?.isLocked) {
      confirmedColumns.push(eleColumn);
    } else {
      const pendingColumn = !columnMap[i]?.isIgnored && columnMap[i]?.column;

      if (pendingColumn) {
        unconfirmedMappings.push(`${numToAlpha(i + 1)}: ${pendingColumn.label}`);
      }

      unconfirmedColumns.push(eleColumn);
    }
  });

  // Overflow columns never render a card, so the denominator is what is actually mappable.
  const mappableCount = confirmedColumns.length + unconfirmedColumns.length;

  // Locking a card removes it from the unconfirmed list and shifts the rest up, so re-anchor on
  // whatever the user needs next: 'nearest' leaves an already-visible target alone.
  useEffect(() => {
    if (scrollTarget === null) return;

    const node = scrollTarget === 'actions' ? eleActions.current : eleColumns.current[scrollTarget];

    node?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    setScrollTarget(null);
  }, [scrollTarget]);

  // Continuing with matched-but-unconfirmed columns silently drops their data at submission,
  // so the dialog forces an explicit confirm-all or discard first.
  const handleContinue = useCallback(() => {
    if (unconfirmedMappings.length > 0) {
      setShowUnconfirmedDialog(true);
    } else {
      completeMatching();
    }
  }, [unconfirmedMappings.length, completeMatching]);

  const handleConfirmAll = useCallback(() => {
    confirmAllMappings();
    setShowUnconfirmedDialog(false);
    completeMatching();
  }, [confirmAllMappings, completeMatching]);

  const handleDiscard = useCallback(() => {
    discardUnconfirmedMappings();
    setShowUnconfirmedDialog(false);
    completeMatching();
  }, [discardUnconfirmedMappings, completeMatching]);

  const handleCancelDialog = useCallback(() => setShowUnconfirmedDialog(false), []);

  const buttons = (
    <>
      {overflowColumns.length > 0 && (
        <Box m={2}>
          <Notice icon={<WarningIcon style={{ color: warnTextColor }} />}>
            <span style={{ color: warnTextColor }}>
              <IntlMsg
                {...messages.noticeOverflowColumns}
                values={{
                  columnNoun: pluralize(overflowColumns.length, 'Column'),
                  columns: overflowColumns.length > 1
                    ? `${numToAlpha(overflowColumns[0] + 1)}–${numToAlpha(overflowColumns[overflowColumns.length - 1] + 1)}`
                    : numToAlpha(overflowColumns[0] + 1),
                  rowNoun: pluralize(overflowRows.length, 'row'),
                  rows: summarizeList(overflowRows.map(String), OVERFLOW_ROWS_LISTED)
                }}
              />
            </span>
          </Notice>
        </Box>
      )}
      {unmetRequirements.length > 0 && (
        <Box>
          <Typography><IntlMsg {...messages.noticeRequiredRemaining} /></Typography>
          <Box display="flex" p={2} pb={3}>
            {unmetRequirements.map(requirement => (
              <Box key={requirement.key} mr={2}><Chip label={requirement.label} /></Box>
            ))}
          </Box>
          <Box m={2}><Divider /></Box>
        </Box>
      )}
      {unmetRequirements.length === 0 && unmetFields.length !== 0 && confirmedColumns.length < mappableCount && (
        <Box>
          <Box display="flex" m={2} alignItems="center">
            <Notice icon={<Check style={{ color: successColor }} />}><IntlMsg {...messages.noticeOptionalRemaining} /></Notice>
            <Spacer />
            <PrimaryButton onClick={handleContinue}><IntlMsg {...messages.btnContinue} /></PrimaryButton>
          </Box>
          <Box m={2}><Divider /></Box>
        </Box>
      )}
      {unmetRequirements.length === 0 && unmetFields.length === 0 && confirmedColumns.length < mappableCount && (
        <>
          <Box display="flex" m={2} alignItems="center">
            <Notice icon={<Check style={{ color: successColor }} />}><IntlMsg {...messages.noticeAllFieldsMatched} /></Notice>
            <Spacer />
            <PrimaryButton onClick={handleContinue}><IntlMsg {...messages.btnContinue} /></PrimaryButton>
          </Box>
          <Box m={2}><Divider /></Box>
        </>
      )}
      {confirmedColumns.length === mappableCount && unmetRequirements.length === 0 && (
        <>
          <Box display="flex" m={2} alignItems="center">
            <Notice icon={<Check style={{ color: successColor }} />}><IntlMsg {...messages.noticeAllConfirmed} /></Notice>
            <Spacer />
            <PrimaryButton onClick={handleContinue}><IntlMsg {...messages.btnContinue} /></PrimaryButton>
          </Box>
          <Box m={2}><Divider /></Box>
        </>
      )}

      {confirmedColumns.length > 0 && (
        <Box display="flex" m={2}>
          <Typography><Bold>{confirmedColumns.length} of {mappableCount} mappings confirmed</Bold></Typography>
          <Spacer />
          <Link onClick={() => eleMappings?.current?.scrollIntoView()}>View Confirmed Mappings</Link>
        </Box>
      )}
    </>
  );

  return <>
    <UnconfirmedMappingsDialog
      open={showUnconfirmedDialog}
      unconfirmedColumns={unconfirmedMappings}
      onConfirmAll={handleConfirmAll}
      onDiscard={handleDiscard}
      onCancel={handleCancelDialog}
    />

    <div ref={eleActions}>{buttons}</div>

    {columnSelectionOptions}

    {unconfirmedColumns}

    {confirmedColumns.length > 0 && (
      <>
        <Box m={2} mt={4}>
          <Typography ref={eleMappings} variant="h6" component="h6">Confirmed Mappings</Typography>
        </Box>
        {confirmedColumns}
      </>
    )}

    {buttons}
  </>;
};
